import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CleviaLogo } from '@/components/CleviaLogo';
import { cn } from '@/lib/utils';
import { EASE_OUT, Reveal, SplitWords, Stagger, StaggerItem } from './motion-primitives';

/**
 * Kit de mise en page des pages publiques.
 * Direction (identique à la landing) : tokens de thème uniquement, une seule
 * couleur d'accent (brand), typographie dominante, mouvement au service de la
 * lecture. Tout le mouvement se coupe sous `prefers-reduced-motion`.
 */

/* ── Décor de fond : grille + halo de marque ─────────────────────── */

export function HeroBackdrop({ className }: { className?: string }) {
  return (
    <div
      className={cn('pointer-events-none absolute inset-0 -z-10 overflow-hidden', className)}
      aria-hidden="true"
    >
      <div
        className="absolute inset-0 opacity-[0.35] dark:opacity-[0.25] [mask-image:radial-gradient(70%_55%_at_50%_0%,black,transparent)]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <div className="absolute -top-40 left-1/2 h-[520px] w-[880px] -translate-x-1/2 rounded-full bg-brand-400/20 blur-[140px] dark:bg-brand-600/15" />
    </div>
  );
}

/* ── Section : rythme vertical et largeur communs ────────────────── */

const SECTION_WIDTH = {
  narrow: 'max-w-3xl',
  article: 'max-w-4xl',
  default: 'max-w-6xl',
} as const;

export function Section({
  children,
  className,
  innerClassName,
  tone = 'default',
  bordered = true,
  width = 'default',
  id,
}: {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
  tone?: 'default' | 'muted' | 'dark';
  bordered?: boolean;
  width?: keyof typeof SECTION_WIDTH;
  id?: string;
}) {
  return (
    <section
      id={id}
      className={cn(
        'relative px-6 py-24 lg:py-32',
        tone === 'muted' && 'bg-muted/30',
        tone === 'dark' && 'overflow-hidden bg-gray-950 dark:bg-white/[0.03]',
        bordered && (tone === 'dark' ? 'border-y border-white/10' : 'border-b border-border'),
        className
      )}
    >
      {tone === 'dark' && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#16c47f]/50 to-transparent"
          aria-hidden="true"
        />
      )}
      <div className={cn('mx-auto', SECTION_WIDTH[width], innerClassName)}>{children}</div>
    </section>
  );
}

/* ── En-tête de section ──────────────────────────────────────────── */

export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  tone = 'light',
  className,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  align?: 'left' | 'center';
  tone?: 'light' | 'dark';
  className?: string;
}) {
  return (
    <Stagger
      className={cn(align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl', className)}
      stagger={0.07}
    >
      {eyebrow && (
        <StaggerItem>
          <p
            className={cn(
              'text-[11px] font-medium uppercase tracking-[0.22em]',
              tone === 'dark' ? 'text-gray-500' : 'text-muted-foreground'
            )}
          >
            {eyebrow}
          </p>
        </StaggerItem>
      )}
      <StaggerItem>
        <h2
          className={cn(
            'text-balance text-3xl font-semibold leading-[1.12] tracking-tight sm:text-4xl lg:text-[2.75rem]',
            eyebrow && 'mt-4',
            tone === 'dark' ? 'text-white' : 'text-foreground'
          )}
        >
          {title}
        </h2>
      </StaggerItem>
      {subtitle && (
        <StaggerItem>
          <p
            className={cn(
              'mt-5 text-pretty text-base leading-relaxed sm:text-lg',
              tone === 'dark' ? 'text-gray-400' : 'text-muted-foreground'
            )}
          >
            {subtitle}
          </p>
        </StaggerItem>
      )}
    </Stagger>
  );
}

/* ── Hero de page ────────────────────────────────────────────────── */

export function PageHero({
  eyebrow,
  title,
  highlight,
  subtitle,
  meta,
  actions,
  align = 'center',
  width = 'default',
  children,
  className,
}: {
  eyebrow?: string;
  title: string;
  /** Seconde partie du titre, peinte à la couleur de marque. */
  highlight?: string;
  subtitle?: string;
  /** Ligne discrète sous le sous-titre (date de lecture, garanties…). */
  meta?: ReactNode;
  actions?: ReactNode;
  align?: 'left' | 'center';
  /** À aligner sur la largeur des sections qui suivent (articles : `narrow`). */
  width?: keyof typeof SECTION_WIDTH;
  children?: ReactNode;
  className?: string;
}) {
  const centered = align === 'center';
  const highlightDelay = 0.1 + title.split(' ').length * 0.055;

  return (
    <section
      className={cn('relative px-6 pb-16 pt-28 lg:pb-24 lg:pt-40', className)}
      data-align={align}
    >
      <HeroBackdrop />

      <div className={cn('mx-auto', SECTION_WIDTH[width], centered && 'text-center')}>
        {eyebrow && (
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE_OUT }}
            className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground"
          >
            {eyebrow}
          </motion.p>
        )}

        <h1
          className={cn(
            'text-balance text-4xl font-semibold leading-[1.06] tracking-tight sm:text-5xl lg:text-[3.5rem]',
            centered && 'mx-auto max-w-4xl',
            !centered && 'max-w-4xl',
            eyebrow && 'mt-5'
          )}
        >
          <SplitWords text={title} delay={0.1} />
          {highlight && (
            <>
              {' '}
              <SplitWords
                text={highlight}
                delay={highlightDelay}
                wordClassName={() => 'text-brand-700 dark:text-brand-400'}
              />
            </>
          )}
        </h1>

        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: EASE_OUT }}
            className={cn(
              'mt-7 text-pretty text-lg leading-relaxed text-muted-foreground',
              centered ? 'mx-auto max-w-2xl' : 'max-w-2xl'
            )}
          >
            {subtitle}
          </motion.p>
        )}

        {meta && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className={cn(
              'mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground',
              centered && 'justify-center'
            )}
          >
            {meta}
          </motion.div>
        )}

        {actions && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62, ease: EASE_OUT }}
            className={cn(
              'mt-10 flex flex-col gap-3 sm:flex-row',
              centered ? 'items-center justify-center' : 'items-start'
            )}
          >
            {actions}
          </motion.div>
        )}

        {children}
      </div>
    </section>
  );
}

/* ── Séparateurs de points pour les lignes de garanties ──────────── */

export function MetaDot() {
  return <span className="h-1 w-1 rounded-full bg-border" aria-hidden="true" />;
}

/* ── Boutons ─────────────────────────────────────────────────────── */

export function PrimaryCta({
  to,
  href,
  children,
  className,
  shineLoop = false,
  fullWidthOnMobile = true,
}: {
  to?: string;
  href?: string;
  children: ReactNode;
  className?: string;
  /** Reflet qui repasse en boucle — réservé aux CTA de fin de page. */
  shineLoop?: boolean;
  fullWidthOnMobile?: boolean;
}) {
  const button = (
    <Button
      size="lg"
      className={cn(
        'group relative gap-2 overflow-hidden px-8 shadow-lg shadow-brand-600/20 transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0',
        fullWidthOnMobile && 'w-full sm:w-auto',
        className
      )}
    >
      <span className="relative z-10 flex items-center gap-2">
        {children}
        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-y-0 -left-full w-1/2 bg-white/25 blur-md',
          shineLoop ? 'motion-safe:animate-shine-loop' : 'motion-safe:group-hover:animate-shine'
        )}
      />
    </Button>
  );

  if (href) {
    return (
      <a href={href} className={fullWidthOnMobile ? 'w-full sm:w-auto' : undefined}>
        {button}
      </a>
    );
  }

  return (
    <Link to={to ?? '/'} className={fullWidthOnMobile ? 'w-full sm:w-auto' : undefined}>
      {button}
    </Link>
  );
}

export const GHOST_CTA_CLASS =
  'inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-border px-7 text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 hover:bg-muted sm:w-auto';

export function SecondaryCta({
  to,
  href,
  children,
  className,
}: {
  to?: string;
  href?: string;
  children: ReactNode;
  className?: string;
}) {
  if (href) {
    return (
      <a href={href} className={cn(GHOST_CTA_CLASS, className)}>
        {children}
      </a>
    );
  }

  return (
    <Link to={to ?? '/'} className={cn(GHOST_CTA_CLASS, className)}>
      {children}
    </Link>
  );
}

/* ── FAQ ─────────────────────────────────────────────────────────── */

export function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <StaggerItem>
      <div className="border-b border-border">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="group flex w-full items-start justify-between gap-6 py-6 text-left"
        >
          <span className="text-balance text-base font-medium transition-colors duration-200 group-hover:text-brand-700 dark:group-hover:text-brand-400 sm:text-lg">
            {question}
          </span>
          <span
            className={cn(
              'mt-1 shrink-0 rounded-full border border-border p-1.5 text-muted-foreground transition-all duration-300',
              open ? 'rotate-180 border-primary text-primary' : 'group-hover:border-primary/40'
            )}
          >
            {open ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          </span>
        </button>
        <div
          className={cn(
            'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
            open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <div className="overflow-hidden">
            <p className="max-w-2xl text-pretty pb-6 pr-6 leading-relaxed text-muted-foreground">
              {answer}
            </p>
          </div>
        </div>
      </div>
    </StaggerItem>
  );
}

/** Section FAQ complète : titre collant à gauche, questions à droite. */
export function FaqSection({
  eyebrow,
  title,
  subtitle,
  items,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  items: { q: string; a: string }[];
}) {
  return (
    <section className="border-b border-border px-6 py-24 lg:py-32">
      <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
        <div>
          <div className="lg:sticky lg:top-28">
            <SectionHeading eyebrow={eyebrow} title={title} subtitle={subtitle} />
          </div>
        </div>

        <Stagger className="border-t border-border" stagger={0.06}>
          {items.map((item) => (
            <FaqItem key={item.q} question={item.q} answer={item.a} />
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ── CTA de fin de page ──────────────────────────────────────────── */

export function CtaSection({
  title,
  subtitle,
  action,
  secondaryAction,
  note,
}: {
  title: string;
  subtitle?: string;
  action: { label: string; to?: string; href?: string };
  secondaryAction?: { label: string; to?: string; href?: string };
  note?: string;
}) {
  return (
    <section className="px-6 py-24 lg:py-32">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <div className="relative isolate overflow-hidden rounded-2xl border border-border bg-card px-8 py-16 text-center sm:px-16">
            {/* Halo de marque qui tourne lentement derrière la carte */}
            <div
              className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
              aria-hidden="true"
            >
              <div className="absolute left-1/2 top-0 h-[140%] w-[140%] -translate-x-1/2 -translate-y-1/3 opacity-[0.18] motion-safe:animate-aurora-spin [animation-duration:22s] dark:opacity-[0.22]">
                <div className="h-full w-full bg-[conic-gradient(from_0deg,transparent_0deg,#16c47f_60deg,transparent_140deg,transparent_220deg,#16c47f_290deg,transparent_360deg)] blur-3xl" />
              </div>
            </div>
            {/* Filet lumineux sur le bord haut */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent"
              aria-hidden="true"
            />

            <Stagger stagger={0.09}>
              <StaggerItem>
                <h2 className="mx-auto max-w-2xl text-balance text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-[2.75rem]">
                  {title}
                </h2>
              </StaggerItem>
              {subtitle && (
                <StaggerItem>
                  <p className="mx-auto mt-5 max-w-xl text-pretty leading-relaxed text-muted-foreground">
                    {subtitle}
                  </p>
                </StaggerItem>
              )}
              <StaggerItem className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <PrimaryCta
                  to={action.to}
                  href={action.href}
                  shineLoop
                  fullWidthOnMobile={false}
                  className="px-9"
                >
                  {action.label}
                </PrimaryCta>
                {secondaryAction && (
                  <SecondaryCta to={secondaryAction.to} href={secondaryAction.href}>
                    {secondaryAction.label}
                  </SecondaryCta>
                )}
              </StaggerItem>
              {note && (
                <StaggerItem>
                  <p className="mt-6 text-sm text-muted-foreground">{note}</p>
                </StaggerItem>
              )}
            </Stagger>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ── Pages de contenu long (légal, CMS, articles) ────────────────── */

/**
 * Typographie d'article branchée sur les tokens de thème.
 * Utilisée par les pages légales et le rendu CMS public.
 */
export const PROSE_CLASS = [
  'prose max-w-none dark:prose-invert',
  'prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-foreground',
  'prose-h2:text-2xl prose-h2:mt-14 prose-h2:mb-4 prose-h2:pb-3 prose-h2:border-b prose-h2:border-border',
  'prose-h3:text-lg prose-h3:mt-10',
  'prose-p:text-muted-foreground prose-p:leading-relaxed',
  'prose-li:text-muted-foreground prose-strong:text-foreground',
  'prose-a:text-brand-700 dark:prose-a:text-brand-400 prose-a:no-underline hover:prose-a:underline',
  'prose-hr:border-border prose-blockquote:border-l-primary/40 prose-blockquote:text-foreground',
].join(' ');

/** Barre supérieure minimale des pages de contenu autonomes (légal, CMS). */
export function DocumentTopBar({
  onBack,
  backLabel,
  onHome,
}: {
  onBack: () => void;
  backLabel: string;
  onHome: () => void;
}) {
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <button
          onClick={onBack}
          className="group inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
          {backLabel}
        </button>
        <button onClick={onHome} className="transition-opacity hover:opacity-80">
          <CleviaLogo size="sm" />
        </button>
        <div className="w-16" aria-hidden="true" />
      </div>
    </header>
  );
}

/** Pied de page minimal, pendant de `DocumentTopBar`. */
export function DocumentFooter() {
  return (
    <footer className="border-t border-border py-10">
      <div className="mx-auto max-w-6xl px-6 text-center">
        <CleviaLogo size="sm" className="mb-3 justify-center" />
        <p className="text-sm text-muted-foreground">
          © {new Date().getFullYear()} clevia. Tous droits réservés.
        </p>
      </div>
    </footer>
  );
}

/* ── Écrans d'authentification ───────────────────────────────────── */

/** Classe commune des champs de formulaire des écrans publics. */
export const FIELD_CLASS =
  'h-12 rounded-lg border-border bg-muted/40 text-foreground placeholder:text-muted-foreground focus-visible:ring-primary/30 focus-visible:ring-offset-0';

/** Champ avec icône à gauche : `FIELD_CLASS` + réserve pour l'icône. */
export const FIELD_WITH_ICON_CLASS = `${FIELD_CLASS} pl-11`;

export function AuthLayout({
  title,
  subtitle,
  children,
  back,
  footer,
  aside,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  back?: { to: string; label: string };
  footer?: ReactNode;
  /** Ligne discrète sous la carte (badge de sécurité, aide…). */
  aside?: ReactNode;
}) {
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-background px-6 py-16">
      <HeroBackdrop />

      <div className="w-full max-w-md">
        {back && (
          <Link
            to={back.to}
            className="group mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
            {back.label}
          </Link>
        )}

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: EASE_OUT }}
          className="relative isolate overflow-hidden rounded-2xl border border-border bg-card p-8 shadow-sm sm:p-10"
        >
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"
            aria-hidden="true"
          />

          <div className="text-center">
            <CleviaLogo size="lg" className="justify-center" />
            <h1 className="mt-6 text-balance text-2xl font-semibold tracking-tight sm:text-[1.75rem]">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-3 text-pretty text-sm leading-relaxed text-muted-foreground">
                {subtitle}
              </p>
            )}
          </div>

          <div className="mt-8">{children}</div>

          {footer && <div className="mt-8 text-center text-sm">{footer}</div>}
        </motion.div>

        {aside && (
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            {aside}
          </div>
        )}
      </div>
    </div>
  );
}
